/**
 * useSignedAudioPlayer — reproductor de audios FIRMADOS dirigido por foco, con UN
 * solo elemento <audio> compartido por una lista de ítems. Extraído de la mesa de
 * revisión clínica (M11) para reutilizarlo en la vista rápida de la ficha, la
 * vista global de Evaluaciones y el reporte del terapeuta.
 *
 * El hook NO renderiza nada: el consumidor pinta un único <audio {...audioProps}>
 * (con o sin `controls`) y los botones por fila que llaman a `irAItem`/`alternar`.
 *
 * Errores del <audio> y URLs firmadas (anti-LOOP, por construcción):
 * un enlace firmado vencido (403) se recupera re-firmando (el consumidor recarga
 * en `onExpired`). Pero un archivo corrupto/vacío falla igual, y re-firmar no lo
 * arregla: cada reload genera una URL NUEVA, así que un guard por-URL nunca corta
 * el bucle (fue el bug post-#59). Por eso:
 *   1. Guard POR-ÍTEM (no por-URL): cada ítem tiene derecho a UNA sola re-firma;
 *      no se resetea al recargar. Segundo error del mismo ítem → "no reproducible".
 *   2. Tope GLOBAL de re-firmas por sesión de página (monótono): backstop duro —
 *      a lo sumo N reloads en toda la vida de la página.
 *   3. `MEDIA_ERR_DECODE` (los bytes SÍ se descargaron pero no decodifican →
 *      corrupto inequívoco) corta a CERO reloads y marca "no reproducible". Los
 *      demás códigos son ambiguos entre 403-vencido y contenedor-ilegible (ambos
 *      suelen dar code 4 en Chromium), así que reciben la única re-firma.
 * El `<audio>` debería montarse con `preload="none"` (ver SharedAudio) para que un
 * archivo malo NO falle en el montaje, sino recién al dar play.
 */
import { useEffect, useRef, useState, type RefObject } from "react";

/** Tope global de re-firmas (reloads) por sesión de página. Una recarga del
 *  workbench re-firma TODAS las URLs a la vez, así que un valor bajo basta: la
 *  expiración es temporal y afecta a todos los enlaces juntos. Backstop anti-loop. */
const MAX_REFIRMAS_GLOBAL = 3;
/** MediaError.code: los bytes se descargaron pero el decodificador falló →
 *  archivo corrupto, re-firmar es inútil (a diferencia de un 403 de URL vencida). */
const MEDIA_ERR_DECODE = 3;

export interface AudioItem {
  id: string;
  audioUrl: string | null;
}

export interface UseSignedAudioPlayerOptions {
  items: readonly AudioItem[];
  /** Auto-avanzar al ítem siguiente al terminar (mesa: sí; vista rápida: no). */
  autoAdvance?: boolean;
  /** URL firmada probablemente vencida: el consumidor recarga datos (re-firma). */
  onExpired?: (src: string) => void;
}

export interface SignedAudioPlayer {
  audioRef: RefObject<HTMLAudioElement | null>;
  /** Índice del ítem en foco, ya clampado al rango de la lista. */
  idx: number;
  /** Ítem en foco (undefined si la lista está vacía). */
  foco: AudioItem | undefined;
  /** Ítems ya reproducidos (para marcar ✓). */
  reproducidos: ReadonlySet<string>;
  /** Ítems cuyo audio NO es reproducible (corrupto/ausente tras agotar re-firma). */
  noReproducibles: ReadonlySet<string>;
  /** ¿El audio en foco está sonando ahora? (para el botón ▶/⏸). */
  reproduciendo: boolean;
  /** Enfoca el índice `i`; `reproducir` dispara la reproducción al enfocar. */
  irA: (i: number, reproducir: boolean) => void;
  /** Enfoca el ítem por id; `reproducir` lo reproduce (re-reproduce si ya es foco). */
  irAItem: (id: string, reproducir: boolean) => void;
  /** Play/pause; con `id`, enfoca y reproduce ese ítem si no era el foco. */
  alternar: (id?: string) => void;
  /** Props del <audio> compartido (renderízalo UNA sola vez). */
  audioProps: {
    ref: RefObject<HTMLAudioElement | null>;
    src: string | undefined;
    onEnded: () => void;
    onPlay: () => void;
    onPause: () => void;
    onError: () => void;
  };
  /** Teclado: espacio = play/pausa; →/n = siguiente; ←/p = anterior. */
  onKeyDown: (e: { key: string; preventDefault: () => void }) => void;
}

export function useSignedAudioPlayer(opts: UseSignedAudioPlayerOptions): SignedAudioPlayer {
  const { items, autoAdvance = false, onExpired } = opts;

  const [focoIdx, setFocoIdx] = useState(0);
  const [reproducidos, setReproducidos] = useState<ReadonlySet<string>>(new Set());
  const [noReproducibles, setNoReproducibles] = useState<ReadonlySet<string>>(new Set());
  const [reproduciendo, setReproduciendo] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  const autoPlayRef = useRef(false); // reproducir tras cambiar de foco
  // Guards anti-loop: NUNCA se resetean al re-firmar (ese reset fue el bug post-#59).
  const refirmadoItem = useRef<Set<string>>(new Set()); // ítems que ya gastaron su re-firma
  const refirmasGlobales = useRef(0); // total de re-firmas en la vida de la página
  const reproduccionActiva = useRef(false); // ¿el play actual lo inició el usuario / auto-avance?
  const enCadena = useRef(false); // ¿venimos de una cadena de auto-avance? (para saltar ítems malos)

  const idx = Math.min(focoIdx, Math.max(0, items.length - 1));
  const foco = items[idx];

  function reproducirFoco() {
    const el = audioRef.current;
    if (el && foco?.audioUrl && !noReproducibles.has(foco.id)) {
      reproduccionActiva.current = true;
      void el.play().catch(() => undefined);
    }
  }

  // Al cambiar de foco: apaga el indicador y, si venía marcado, reproduce.
  useEffect(() => {
    setReproduciendo(false);
    if (!autoPlayRef.current) return;
    autoPlayRef.current = false;
    reproducirFoco();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [foco?.id, foco?.audioUrl]);

  function marcar(id: string) {
    setReproducidos((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }
  function marcarNoReproducible(id: string) {
    setNoReproducibles((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }

  function irA(i: number, reproducir: boolean) {
    setFocoIdx(i);
    autoPlayRef.current = reproducir;
  }

  function irAItem(id: string, reproducir: boolean) {
    enCadena.current = false; // interacción manual: no es una cadena de auto-avance
    const i = items.findIndex((x) => x.id === id);
    if (i < 0) return;
    if (i === idx && reproducir) {
      reproducirFoco(); // ya es el foco: reproducir directo
      return;
    }
    irA(i, reproducir);
  }

  function alternar(id?: string) {
    if (id && id !== foco?.id) {
      irAItem(id, true); // enfoca y reproduce el nuevo (detiene el anterior)
      return;
    }
    enCadena.current = false;
    const el = audioRef.current;
    if (!el || !foco?.audioUrl || noReproducibles.has(foco.id)) return;
    if (el.paused) {
      reproduccionActiva.current = true;
      void el.play().catch(() => undefined);
    } else {
      el.pause();
    }
  }

  function onEnded() {
    reproduccionActiva.current = false;
    if (foco) marcar(foco.id);
    if (autoAdvance && idx < items.length - 1) {
      enCadena.current = true; // reproducción continua: seguimos una cadena
      irA(idx + 1, true);
    } else {
      enCadena.current = false;
    }
  }

  function onError() {
    const item = foco;
    if (!item) return;
    const id = item.id;
    const activa = reproduccionActiva.current;
    reproduccionActiva.current = false;
    if (noReproducibles.has(id)) return; // ya conocido como malo

    const code = audioRef.current?.error?.code;
    const decodeInequivoco = code === MEDIA_ERR_DECODE; // bytes bajaron pero no decodifican
    const yaRefirmado = refirmadoItem.current.has(id);
    const capado = refirmasGlobales.current >= MAX_REFIRMAS_GLOBAL;

    // Sin re-firma posible/útil → marcar "no reproducible"; si estábamos en una
    // cadena de auto-avance, saltar al siguiente para no bloquear la revisión.
    if (decodeInequivoco || yaRefirmado || capado || !item.audioUrl) {
      marcarNoReproducible(id);
      if (activa && autoAdvance && enCadena.current && idx < items.length - 1) {
        irA(idx + 1, true); // enCadena sigue true: la cadena continúa saltando malos
      } else {
        enCadena.current = false;
      }
      return;
    }

    // Código ambiguo (posible URL vencida 403): UNA única re-firma por ítem.
    refirmadoItem.current.add(id);
    refirmasGlobales.current += 1;
    autoPlayRef.current = activa; // si se estaba reproduciendo, reanuda tras re-firmar
    onExpired?.(item.audioUrl);
  }

  function onKeyDown(e: { key: string; preventDefault: () => void }) {
    if (e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      alternar();
    } else if (e.key === "ArrowRight" || e.key === "n") {
      if (idx < items.length - 1) irAItem(items[idx + 1]!.id, true);
    } else if (e.key === "ArrowLeft" || e.key === "p") {
      if (idx > 0) irAItem(items[idx - 1]!.id, true);
    }
  }

  return {
    audioRef,
    idx,
    foco,
    reproducidos,
    noReproducibles,
    reproduciendo,
    irA,
    irAItem,
    alternar,
    audioProps: {
      ref: audioRef,
      src: foco?.audioUrl ?? undefined,
      onEnded,
      onPlay: () => {
        setReproduciendo(true);
        reproduccionActiva.current = true;
        // Reproducción exitosa: el ítem SÍ funciona; libera su guard de re-firma
        // para que una expiración genuina futura tenga otra oportunidad.
        if (foco) {
          marcar(foco.id);
          refirmadoItem.current.delete(foco.id);
        }
      },
      onPause: () => setReproduciendo(false),
      onError,
    },
    onKeyDown,
  };
}
